import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MenuContainerComponent } from './MenuContainer';
import { MainMenuContext } from './MainMenuContext';
import { useMenuMode } from './MenuModeContext';
import { ButtonCollapse } from './ButtonCollapse';

const COLLAPSED_KEY = 'menu-collapsed';
const AUTO_COLLAPSE_WIDTH = 1200;

const MenuFooter = styled.footer`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.space[6]}px;
  min-height: ${({ theme }) => theme.space[6]}px;
  margin-top: auto;
`;

export interface MainMenuProps {
  children: ReactNode;
}

export function MainMenu({ children }: MainMenuProps) {
  const mode = useMenuMode();
  const isDrawer = mode === 'drawer';

  const [openSection, setOpenSection] = useState<string | null>(null);

  const [collapsed, setCollapsed] = useState(() => {
    if (isDrawer) return false;
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  });

  useEffect(() => {
    if (isDrawer) return;

    if (window.innerWidth <= AUTO_COLLAPSE_WIDTH) {
      setCollapsed(true);
    }

    const handleResize = () => {
      if (window.innerWidth <= AUTO_COLLAPSE_WIDTH) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDrawer]);

  const handleToggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const effectiveCollapsed = isDrawer ? false : collapsed;

  return (
    <MainMenuContext.Provider value={{ openSection, setOpenSection, collapsed: effectiveCollapsed }}>
      <MenuContainerComponent
        collapsed={effectiveCollapsed}
        fluid={isDrawer}
        footer={
          !isDrawer && (
            <MenuFooter>
              <ButtonCollapse collapsed={collapsed} onClick={handleToggleCollapse} />
            </MenuFooter>
          )
        }
      >
        {children}
      </MenuContainerComponent>
    </MainMenuContext.Provider>
  );
}
